import { v4 as Uuid } from 'uuid'
import AssetFactory from '@/editor/factory.js'
import { VYBY_EFFECTS } from '@/etc/consts'
import { fromStorage } from '@/etc/storage'
import GLOBAL_EFFECT_PRESETS from '@/editor/effects/globalEffectPresets'
import GLOBAL_TEXT_MOTION_PRESETS from '@/editor/text/globalTextMotionPresets'
import { buildEffectCatalog } from '@/editor/effects/effectPresets'

// Shared factory instance for project store consumers
export const factory = new AssetFactory()

// Track identifiers (stable for initial state)
export const trackUuid1 = Uuid()
export const trackUuid2 = Uuid()
export const trackUuid3 = Uuid()
export const trackUuid4 = Uuid()
export const audioTrackUuid = Uuid()
export const globalRigTrackUuid = Uuid()

export const tracksIndex = {
  trackUuid1,
  trackUuid2,
  trackUuid3,
  trackUuid4,
  audioTrackUuid,
  globalRigTrackUuid,
}

export const defaultSong = () => ({
  title: null,
  artist: null,
  bpm: 0,
  data: null,
})

export const defaultEnv = () => ({
  time: null,
  location: null,
  camera: null,
  color: null,
  descripion: null,
})

export const defaultPicture = () => ({
  heroes: [],
  action: null,
  style: null,
  director: null,
  more: '',
})

export const defaultSettings = () => ({
  width: 896,
  height: 512,
  aspect: { w: 7, h: 4 },
  fps: 24,
  bpm: 120,
  qps: 4, // quarters per signature
  spl: 4, // signatures per loop
  // Scene (global)
  background: '#111111',
  ambientPower: 1.3,
  ambientColor: '#ffffff',
  // Postprocessing (pmndrs/postprocessing). When disabled, editor uses raw renderer.render().
  postprocessingEnabled: true,
  // UI: stroke color for editor panel strokes (vyby-stroke-radial)
  strokeColor: '#ffffff',
  paintToolMode: 'stroke',
  paintBrushColor: '#f37021',
  paintBrushSize: 12,
  paramColors: {},
  title: '',

  autoAdoptBPM: false,
  // Phase 0 feature flags (no behavior change by default)
  // Legacy Keyframes V2 toggle (off by default; enables new evaluator when true)
  legacyKeyframesV2: true,
  smartKeyGroupsEnabled: false,
  // Phase V2: show QuickBar for V2 keyframing when SmartKeys are disabled
  v2QuickBarEnabled: true,
  // Phase 1: default scope for beat-locked micro/impact loops ('global' | 'local')
  smartBeatLockScope: 'global',
  speedTrimEnabled: true,
  loopBeatLockDefault: 'beat-locked',
  // Phase 9: dev validation guard toggle
  devValidationEnabled: false,
  // WebCodecs audio decode via SAB/worklet (preview). Kill-switch: when false,
  // every strip plays audio via its HTML element.
  useWebCodecsAudioPreview: true,
  // Video Frame Engine V2 — informational only; the engine is the only video
  // path (legacy useWebCodecsPreview/useUnifiedTrackBuffer settings removed;
  // deferred extraction is now the fixed behavior).
  useVideoFrameEngineV2: true,
  // Playback stop behavior ('stay' keeps playhead where pause/stop happened; future option may use 'return_to_start').
  playStopBehavior: 'stay',
  // Track Z spacing preset (Phase 1-2: data model only; runtime uses it in later phases)
  trackZPreset: 'Singularity',

  // Flora-style node canvas graph (Phase 4). Stored in project.settings so it persists with the project.
  // Shape: { version: number, nodes: Node[], edges: Edge[] } | null
  nodeGraph: null,
  assetSources: {},
  assetAddedAt: {},
  projectBanner: null,
  projectSlotIndex: null,
})

export const defaultTrack = (uuid, type, layer, name, accept, opts = {}) => ({
  type,
  uuid,
  layer,
  depth: 0,
  maxDepth: 0,
  // Per-track Z offset (track-level "push" with size-preserve behavior in later phases)
  zOffset: 0,
  collapsible: true,
  fx: false,
  visible: true,
  name,
  accept,
  strips: {},
  ...opts,
})

export const defaultTimeline = () => ({
  startTime: 0,
  endTime: 60,
  length: 60,
  viewMax: 90,
  exportIn: 0,
  exportOut: 60,
  exportAuto: true,
  // Phase 9: user-controlled export filename + codec. The FileSystemFileHandle
  // chosen via "Choose location…" is intentionally NOT in store — handles are
  // not serializable and must live on the PropsPanel component instance.
  exportFilename: 'video',
  exportCodec: 'h264',
  currentTime: 0,
  activeTrack: tracksIndex.trackUuid1,
  activeStrip: null,
  selectedStrips: [],
  activeEffect: null,
  activeKeyframe: null,
  // Keyframe lane selection (used to delete only selected props like NLEs)
  // Shape: { uuid, lane, props: string[] } | null
  activeKeyframeSelection: null,
  activeSmartKey: null,
  // UI: ultra-expanded single-track modal state (Phase 1)
  ultraExpand: { open: false, trackUuid: null },
  zeroFrame3D: { width: 0, height: 0 },
  activeAudio: null,
  _animFrame: null,
})

export const createDefaultState = () => ({
  initialized: false,
  isNew: true,
  uuid: null,
  redirect: false,
  saved: true,
  autosave: {
    status: 'idle',
    error: null,
    lastSavedAt: null,
  },
  assetHydration: {
    active: false,
    projectId: null,
    loaded: 0,
    total: 0,
    error: null,
  },
  song: defaultSong(),
  environment: defaultEnv(),
  picture: defaultPicture(),
  generated: {},
  library: {},
  // Proxy video (Proxy_Video.md Phase 2): per-asset detection/transcode state.
  // uuid -> { status: 'not-needed'|'awaiting-persist'|'requesting'|'ready'|'error',
  //           syncCount, durationSec, ratio, proxyPath, error }
  videoProxy: {},
  globalPresetCatalogRows: [],
  globalPresetCatalogLoaded: false,
  globalPresetCatalogError: null,
  globalEffectPresets: GLOBAL_EFFECT_PRESETS,
  globalTextMotionPresets: GLOBAL_TEXT_MOTION_PRESETS,
  globalNodeWorkflowPresets: [],
  globalPersonalityPresets: [],
  globalMoodPresets: [],
  // Merge source-controlled Vyby presets with stored user presets.
  effects: buildEffectCatalog({ ...VYBY_EFFECTS }, GLOBAL_EFFECT_PRESETS, fromStorage('vyby/effects', null)),
  settings: defaultSettings(),
  tracks: {
    // Hidden system track: holds always-on Scene + Camera strips (not shown in timeline by default)
    [tracksIndex.globalRigTrackUuid]: defaultTrack(
      tracksIndex.globalRigTrackUuid,
      'global',
      99,
      'Rig',
      ['camera'],
      { collapsible: false, visible: false, hidden: true, locked: true }
    ),
    [tracksIndex.trackUuid1]: defaultTrack(
      tracksIndex.trackUuid1,
      'layer',
      1,
      'Front close',
      ['video', 'image', 'text', 'adjustment', 'model']
    ),
    [tracksIndex.trackUuid2]: defaultTrack(
      tracksIndex.trackUuid2,
      'layer',
      2,
      'Front behind',
      ['video', 'image', 'text', 'adjustment', 'model']
    ),
    [tracksIndex.trackUuid3]: defaultTrack(
      tracksIndex.trackUuid3,
      'layer',
      3,
      'Back close',
      ['video', 'image', 'text', 'adjustment', 'model']
    ),
    [tracksIndex.trackUuid4]: defaultTrack(
      tracksIndex.trackUuid4,
      'layer',
      4,
      'Back far',
      ['video', 'image', 'text', 'adjustment', 'model']
    ),
    [tracksIndex.audioTrackUuid]: defaultTrack(
      tracksIndex.audioTrackUuid,
      'audio',
      0,
      'Audio',
      ['audio']
    ),
  },
  timeline: defaultTimeline(),
  dnd: null,
  tool: [],
  gizmo: false,
  akfValues: {},
  akfSource: null,
  playing: false,
  // Records where the most recent playback run began so we can optionally snap back later.
  lastPlaybackStartTime: 0,
  recording: false,
  calculating: false,
  tune: 1,
  tuned: false,
  fullScreen: false,
  fullScreenRect: null,
  snap2beat: true,
  snapUnit: 'frame',
  effectsView: false,
  flatView: true,
  gridHelper: true,
  cameraStrip: null,
  // Bottom lane under preview: switch between audio lane and camera lane (Phase 2)
  bottomLaneMode: 'audio',
  // Transcript warp Phase 3: taller audio lane for transcript/waveform editing
  audioLaneExpanded: false,
  currentSelection: null,
  clipRangeEdit: { active: false, stripId: null },
  textModal: { open: false, stripId: null, source: null },
  subclipModal: { open: false, stripId: null, source: null, payload: null },
  subclipModalResult: null,
  // Phase 3: selected smart region on a strip prop (for micro/impact UI)
  selectedSmartRegion: null,
  settingsView: false,
  aiTool: null,
  aiTask: null,
  generate: {
    on: false,
    tool: '',
    cfg: {},
  },
  _history: { undo: [], redo: [] },
})

